/**
 * Text utility functions for StudySync
 */

/**
 * Truncates text to a specified character limit and adds ellipsis if needed
 * @param text - The text to truncate
 * @param maxLength - Maximum number of characters
 * @returns Truncated text with ellipsis if needed
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (!text || text.length <= maxLength) {
    return text;
  }
  return text.substring(0, maxLength) + '...';
};

/**
 * Splits text into lines with word-based wrapping
 * @param text - The text to split
 * @param maxCharsPerLine - Maximum characters per line
 * @param maxLines - Maximum number of lines
 * @returns Array of text lines
 */
export const splitTextIntoLines = (
  text: string, 
  maxCharsPerLine: number, 
  maxLines: number
): string[] => {
  if (!text) return [];
  
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';
  
  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    
    if (testLine.length <= maxCharsPerLine) {
      currentLine = testLine;
    } else {
      if (currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        // Single word is longer than max chars, add it anyway
        lines.push(word);
        currentLine = '';
      }
      
      if (lines.length >= maxLines) {
        break;
      }
    }
  }
  
  if (currentLine && lines.length < maxLines) {
    lines.push(currentLine);
  }
  
  // If we have more content and reached max lines, add ellipsis to last line
  if (lines.length === maxLines && (currentLine || words.length > lines.reduce((acc, line) => acc + line.split(' ').length, 0))) {
    const lastLine = lines[lines.length - 1];
    if (lastLine.length > maxCharsPerLine - 3) {
      lines[lines.length - 1] = truncateText(lastLine, maxCharsPerLine - 3) + '...';
    } else {
      lines[lines.length - 1] = lastLine + '...';
    }
  }
  
  return lines;
};

/**
 * Formats phone number to (000)-0000-000 format
 * @param value - Raw phone number input
 * @returns Formatted phone number string
 */
export const formatPhoneNumber = (value: string): string => {
  // Remove all non-digit characters
  const phoneNumber = value.replace(/\D/g, '');
  
  // Limit to 10 digits
  const limitedPhoneNumber = phoneNumber.substring(0, 10);
  
  // Format based on length
  if (limitedPhoneNumber.length === 0) {
    return '';
  } else if (limitedPhoneNumber.length <= 3) {
    return `(${limitedPhoneNumber}`;
  } else if (limitedPhoneNumber.length <= 6) {
    return `(${limitedPhoneNumber.substring(0, 3)})-${limitedPhoneNumber.substring(3)}`;
  } else {
    return `(${limitedPhoneNumber.substring(0, 3)})-${limitedPhoneNumber.substring(3, 6)}-${limitedPhoneNumber.substring(6)}`;
  }
};

/**
 * Validates if a phone number is complete and properly formatted
 * @param phoneNumber - Formatted phone number
 * @returns True if phone number is complete (10 digits)
 */
export const isCompletePhoneNumber = (phoneNumber: string): boolean => {
  const digits = phoneNumber.replace(/\D/g, '');
  return digits.length === 10;
};

/**
 * Extracts raw digits from formatted phone number
 * @param formattedPhone - Formatted phone number like "(000)-0000-000"
 * @returns Raw digits only
 */
export const extractPhoneDigits = (formattedPhone: string): string => {
  return formattedPhone.replace(/\D/g, '');
};

/**
 * Formats study name for display with proper line wrapping
 * Handles edge case of single long words by forcing character truncation
 * @param name - Study name
 * @returns Object with formatted lines
 */
export const formatStudyName = (name: string): { lines: string[]; hasOverflow: boolean } => {
  const maxCharsPerLine = 28;
  const maxLines = 2;
  
  if (!name || name.length <= maxCharsPerLine) {
    return { lines: [name], hasOverflow: false };
  }
  
  // First try word-based splitting
  const words = name.split(' ');
  const lines: string[] = [];
  let currentLine = '';
  
  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    
    // If single word is longer than max chars, force character truncation
    if (word.length > maxCharsPerLine) {
      if (currentLine) {
        lines.push(currentLine);
        currentLine = '';
      }
      
      // Force character-based splitting for long words
      let remainingWord = word;
      while (remainingWord.length > 0 && lines.length < maxLines) {
        if (remainingWord.length <= maxCharsPerLine) {
          lines.push(remainingWord);
          break;
        } else {
          const chunk = remainingWord.substring(0, maxCharsPerLine);
          lines.push(chunk);
          remainingWord = remainingWord.substring(maxCharsPerLine);
        }
      }
      
      // If we still have remaining content and reached max lines, add ellipsis
      if (remainingWord.length > 0 && lines.length === maxLines) {
        const lastLine = lines[lines.length - 1];
        lines[lines.length - 1] = lastLine.substring(0, maxCharsPerLine - 3) + '...';
      }
      
      break;
    } else if (testLine.length <= maxCharsPerLine) {
      currentLine = testLine;
    } else {
      if (currentLine) {
        lines.push(currentLine);
        currentLine = word;
      }
      
      if (lines.length >= maxLines) {
        break;
      }
    }
  }
  
  if (currentLine && lines.length < maxLines) {
    lines.push(currentLine);
  }
  
  // Add ellipsis if we have more content
  const totalWordsProcessed = lines.join(' ').split(' ').length;
  const hasOverflow = totalWordsProcessed < words.length || name.length > lines.join('').length;
  
  if (hasOverflow && lines.length === maxLines) {
    const lastLine = lines[lines.length - 1];
    if (lastLine.length > maxCharsPerLine - 3) {
      lines[lines.length - 1] = lastLine.substring(0, maxCharsPerLine - 3) + '...';
    } else {
      lines[lines.length - 1] = lastLine + '...';
    }
  }
  
  return { lines, hasOverflow };
};

/**
 * Formats study description for display with proper word-based line wrapping
 * Lines 1-3: 45 characters each, Line 4: 39 characters
 * @param description - Study description
 * @returns Array of description lines
 */
export const formatStudyDescription = (description: string): string[] => {
  if (!description) return [];
  
  const words = description.split(' ');
  const lines: string[] = [];
  let currentLine = '';
  const maxLines = 4;
  
  for (const word of words) {
    // Lines 1-3 use 45 chars, line 4 uses 39 chars
    const maxCharsForLine = lines.length < 3 ? 45 : 39;
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    
    if (testLine.length <= maxCharsForLine) {
      currentLine = testLine;
    } else {
      if (currentLine) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        // Single word is longer than max chars, add it anyway
        lines.push(word);
        currentLine = '';
      }
      
      if (lines.length >= maxLines) {
        break;
      }
    }
  }
  
  if (currentLine && lines.length < maxLines) {
    lines.push(currentLine);
  }
  
  // If we have more content and reached max lines, add ellipsis to last line
  if (lines.length === maxLines && (currentLine || words.length > lines.reduce((acc, line) => acc + line.split(' ').length, 0))) {
    const lastLine = lines[lines.length - 1];
    if (lastLine.length > 36) { // Leave space for "..."
      lines[lines.length - 1] = truncateText(lastLine, 36) + '...';
    } else {
      lines[lines.length - 1] = lastLine + '...';
    }
  }
  
  return lines;
};